import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';

import {debounce} from '../../../assets/scripts/utils/debounce';

import SearchResults from './search-results';

const Search = ({
  placeholder,
  rootUrl,
  resources,
  unavailableProducts,
  showSearchSuggestions,
  searchSuggestionsTitle,
  searchSuggestions,
  resultMoneyFormat,
  searchIcon,
  typeProductsTitle,
  typeCollectionsTitle,
  typeArticlesTitle,
  typePagesTitle,
  viewAllString,
}) => {
  const [searchTerm, setSearchTerm] = useState();
  const [results, setResults] = useState({products: [], collections: [], pages: [], articles: []});
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef();

  const buildResourceString = (obj) =>
    Object.keys(obj)
      .map((param) => `resources[${param}]=${obj[param]}`)
      .join('&');

  const abortController = useRef();

  const resourceString = buildResourceString(resources);

  const fetchResults = async (term) => {
    const url = `${rootUrl}/search/suggest.json?q=${term}&${resourceString}&resources[options][unavailable_products]=${unavailableProducts}&resources[options][fields]=title,body,product_type,variants.title,vendor,tag`;
    try {
      const response = await fetch(url, {signal: abortController.current.signal});
      if (response.ok === false) {
        throw new Error('Search response was not OK');
      }
      const json = await response.json();
      return json.resources;
    } catch (err) {
      if (err.name === 'AbortError') return null;
      window.console.error(err);
      return null;
    }
  };

  const updateResults = async (term) => {
    try {
      const searchResponse = await fetchResults(term);
      if (searchResponse !== null) {
        setResults({
          products: searchResponse.results.products || [],
          collections: searchResponse.results.collections || [],
          pages: searchResponse.results.pages || [],
          articles: searchResponse.results.articles || [],
        });

        setShowResults(true);
      }
    } catch (err) {
      window.console.error(err);
    }
  };

  const debouncedUpdateResults = useCallback(
    debounce((term) => updateResults(term), 300),
    []
  );

  useEffect(() => inputRef.current.focus(), []);

  useEffect(() => {
    if (abortController.current) {
      abortController.current.abort();
    }

    abortController.current = new AbortController();

    const searchPageResults = document.querySelector('[data-search-page-results]');

    if (!searchTerm) {
      setShowResults(false);
      if (searchPageResults) {
        searchPageResults.classList.remove('hide');
      }
      return;
    }
    if (searchPageResults) {
      searchPageResults.classList.add('hide');
    }

    debouncedUpdateResults(searchTerm);
  }, [searchTerm]);

  const updateSearchTerm = (value) => setSearchTerm(value);

  const handleChange = (evt) => {
    evt.persist();
    updateSearchTerm(evt.target.value);
  };

  const populateSearch = (suggestion) => {
    setSearchTerm(suggestion);
  };

  return (
    <>
      <form className="search__form" action={`${rootUrl}/search`} method="GET" role="search">
        <input type="hidden" name="type" value="product" />
        {searchIcon}
        <input
          className="search__input"
          type="search"
          name="q"
          value={searchTerm}
          placeholder={placeholder}
          onChange={handleChange}
          aria-label={placeholder}
          ref={inputRef}
          enterKeyHint="search"
          autoComplete="off"
        />
      </form>

      {!searchTerm && showSearchSuggestions && searchSuggestions && (
        <>
          {searchSuggestionsTitle && <h4 className="h7">{searchSuggestionsTitle}</h4>}
          <ul class="search__suggestions list-none">
            {searchSuggestions.map((suggestion) => (
              <li key={suggestion}>
                <button type="button" className="btn-none" onClick={() => populateSearch(suggestion)}>
                  {suggestion}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {showResults && (
        <SearchResults
          searchTerm={searchTerm}
          results={results}
          rootUrl={rootUrl}
          moneyFormat={resultMoneyFormat}
          typeProductsTitle={typeProductsTitle}
          typeCollectionsTitle={typeCollectionsTitle}
          typeArticlesTitle={typeArticlesTitle}
          typePagesTitle={typePagesTitle}
          viewAllString={viewAllString}
        />
      )}
    </>
  );
};

// Shopify's Predictive Search API reference:
// https://shopify.dev/docs/themes/ajax-api/reference/predictive-search

Search.propTypes = {
  placeholder: PropTypes.string.isRequired,
  rootUrl: PropTypes.string.isRequired,
  resources: PropTypes.shape({
    type: PropTypes.string,
    limit: PropTypes.number,
  }).isRequired,
  unavailableProducts: PropTypes.string.isRequired,
  showSearchSuggestions: PropTypes.bool,
  searchSuggestionsTitle: PropTypes.string,
  searchSuggestions: PropTypes.arrayOf(PropTypes.string),
  resultMoneyFormat: PropTypes.string.isRequired,
  searchIcon: PropTypes.string,
  typeProductsTitle: PropTypes.string,
  typeCollectionsTitle: PropTypes.string,
  typeArticlesTitle: PropTypes.string,
  typePagesTitle: PropTypes.string,
  viewAllString: PropTypes.string,
};

export default Search;
